import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * VariantPicker component: supports option2 as radio OR dropdown,
 * and auto-fixes unavailable combinations both ways (option1 ↔ option2).
 */
export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #recentAutoFix = false; // guard to avoid immediate repeated auto-fix loops

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles variant input change.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // update UI selection states (radio checked or select selected)
    this.updateSelectedOption(event.target);

    // dispatch a VariantSelectedEvent (keeps compatibility with existing theme events)
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: event.target.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Fetch the updated variant-picker (or product main if combined listing)
    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    // Update the URL's variant query param if the input has a variant id
    const url = new URL(window.location.href);
    let variantId = null;
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (variantId) url.searchParams.set('variant', variantId);
    else url.searchParams.delete('variant');

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Update checked/selected states for inputs and selects
   * Accepts either a DOM element (input/select) or a string id.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Build the fetch request URL for fetching the updated variant-picker HTML.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetch the updated HTML for this variant picker and morph it into place.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Remove defer attribute introduced for initial render
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          // intentionally aborted
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Replace only the variant-picker portion with the new HTML.
   * After morphing, run auto-fix logic to ensure combinations are valid.
   */
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Morph into place
    morph(this, newVariantPickerSource);

    // After morph we need to run the auto-fix on the *new* DOM.
    queueMicrotask(() => {
      // guard: avoid re-applying immediately if we just auto-fixed
      if (this.#recentAutoFix) {
        setTimeout(() => {
          this.#recentAutoFix = false;
        }, 50);
        return;
      }

      //this.autoFixAfterMorph();
    });

    return newProduct;
  }

  /**
   * Run after morph to ensure the selected combination is valid.
   * If a selected group is unavailable, try to auto-select first available value in that group.
   * If none available in that group, try the other group.
   *
   * Supports:
   * - fieldset.variant-option (radio inputs with data-option-available="true|false")
   * - .variant-option--dropdowns (select with disabled options for unavailable values)
   */
autoFixAfterMorph() {
  const optionGroups = Array.from(
    this.querySelectorAll('.variant-option')
  );
  if (optionGroups.length < 2) return;

  const group1 = optionGroups[0];
  const group2 = optionGroups[1];

  /* ---------------------------------------------
     UTILS: detect type + selected value + availability
  ----------------------------------------------*/
  const getSelected = (group) => {
    if (group.querySelector('input[type="radio"]')) {
      const input = group.querySelector('input[type="radio"]:checked');
      return {
        type: "radio",
        group,
        input,
        value: input?.dataset.optionValueId,
        available: input?.dataset.optionAvailable === "true"
      };
    } else {
      const select = group.querySelector("select");
      const opt = select.options[select.selectedIndex];
      return {
        type: "select",
        group,
        select,
        option: opt,
        value: opt?.dataset.optionValueId,
        // dropdown availability → variant exists
        available: !!opt?.dataset.variantId
      };
    }
  };

  const a = getSelected(group1);
  const b = getSelected(group2);

  // If both are valid, nothing to do
  if (a.available && b.available) return;

  /* ---------------------------------------------
     FIX HELPERS
  ----------------------------------------------*/

  const fixRadio = (group) => {
    const first = group.querySelector(
      "input[data-option-available='true']"
    );
    if (!first) return false;

    this.#recentAutoFix = true;
    first.checked = true;
    first.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  };

  const fixSelect = (group) => {
    const select = group.querySelector("select");
    const options = Array.from(select.options);

    // 1. Try next
    for (let i = select.selectedIndex + 1; i < options.length; i++) {
      if (options[i].dataset.variantId) {
        this.#recentAutoFix = true;
        select.value = options[i].value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }
    }

    // 2. Try from start
    for (let i = 0; i < options.length; i++) {
      if (options[i].dataset.variantId) {
        this.#recentAutoFix = true;
        select.value = options[i].value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }
    }

    return false;
  };

  /* ---------------------------------------------
     FIX ORDER
  ----------------------------------------------*/

  // Fix option 1 first if invalid
  if (!a.available) {
    if (a.type === "radio") {
      if (fixRadio(a.group)) return;
    } else {
      if (fixSelect(a.group)) return;
    }
  }

  // Fix option 2 next
  if (!b.available) {
    if (b.type === "radio") {
      if (fixRadio(b.group)) return;
    } else {
      if (fixSelect(b.group)) return;
    }
  }
}



  /**
   * Replace main content (used when combined listings require the whole page to update).
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Helper: get currently selected option input OR select option element
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }
    return selectedOption;
  }

  get selectedOptionId() {
    const selected = this.selectedOption;
    if (!selected) return undefined;
    const { optionValueId } = selected.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
